import {memo} from "react";
import {Flex, HStack, Icon, Stack, Text} from "@chakra-ui/react";
import {DocumentDuplicateIcon, ExclamationTriangleIcon, LockOpenIcon} from "@heroicons/react/20/solid";
import {Button} from "../../../../../components/ui/button";
import {
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "../../../../../components/ui/dialog";

type Entity = "population" | "sample";

/** Per-entity copy: what it's used by, and what unlocking would put at risk. */
const ENTITY_COPY: Record<
  Entity,
  {noun: string; nounPlural: string; usedByNoun: string; usedByPlural: string; impact: string}
> = {
  population: {
    noun: "population",
    nounPlural: "populations",
    usedByNoun: "sample",
    usedByPlural: "samples",
    impact: "Changing its sources, fields, or data will affect those samples — and any tests drawn from them.",
  },
  sample: {
    noun: "sample",
    nounPlural: "samples",
    usedByNoun: "test",
    usedByPlural: "tests",
    impact: "Changing its methodology or selection will affect those tests.",
  },
};

/**
 * Shown when the user tries to unlock a population/sample that something downstream already depends on
 * (a population with linked samples, or a sample with linked tests). It explains that unlocking puts
 * that existing work at risk and steers — but doesn't force — the user towards cloning instead: the
 * clone is the recommended (primary) action, while "Unlock anyway" remains available. No-op prototype:
 * clone doesn't actually fork anything yet.
 */
export const UnlockWarningDialog = memo(
  ({
    open,
    onOpenChange,
    entity,
    usageCount,
    onClone,
    onUnlockAnyway,
  }: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    entity: Entity;
    /** How many downstream things depend on this entity — drives the in-use count + pluralisation. */
    usageCount: number;
    /** Recommended path: make changes on a fresh copy, leaving this entity locked and intact. */
    onClone: () => void;
    /** Proceed with unlocking despite the warning. */
    onUnlockAnyway: () => void;
  }) => {
    const copy = ENTITY_COPY[entity];
    const usedBy = (
      <>
        {usageCount} {usageCount === 1 ? copy.usedByNoun : copy.usedByPlural}
      </>
    );

    return (
      <DialogRoot open={open} onOpenChange={e => onOpenChange(e.open)} placement="center">
        <DialogContent>
          <DialogHeader>
            <HStack gap={3} align="center">
              <Flex
                flex="none"
                align="center"
                justify="center"
                boxSize={9}
                rounded="full"
                colorPalette="orange"
                bg="colorPalette.100"
                color="colorPalette.600"
              >
                <Icon asChild boxSize={5}>
                  <ExclamationTriangleIcon />
                </Icon>
              </Flex>
              <DialogTitle>This {copy.noun} is in use</DialogTitle>
            </HStack>
          </DialogHeader>
          <DialogBody>
            <Stack gap={3}>
              <Text fontSize="sm" color="fg.muted">
                This {copy.noun} is used by {usedBy}. {copy.impact}
              </Text>
              <Text fontSize="sm" color="fg.muted">
                Unlocking and editing it may invalidate work that's already been completed. Clone it instead to make
                your changes on a fresh copy — this {copy.noun} stays locked and intact.
              </Text>
            </Stack>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" colorPalette="gray" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button variant="outline" colorPalette="gray" onClick={onUnlockAnyway}>
              <Icon asChild boxSize="3.5">
                <LockOpenIcon />
              </Icon>
              Unlock anyway
            </Button>
            <Button colorPalette="blue" onClick={onClone}>
              <Icon asChild boxSize="3.5">
                <DocumentDuplicateIcon />
              </Icon>
              Clone instead
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogRoot>
    );
  },
);
